// =============================================================================
// features/sales/campaigns/operate/components/QueueLeaseIndicator.tsx —
// Componente nomeado pelo doc 26 §7 (`QueueLeaseIndicator`): countdown do
// tempo restante da reserva no cabeçalho da operação.
//
// Acessibilidade (briefing do slot #3): a urgência NUNCA depende só da cor —
// o texto ("2:14 restantes", "Reserva expirando…") é a fonte da verdade;
// cor/ícone só reforçam visualmente.
// =============================================================================

import * as React from 'react';

import { cn } from '../../../../../lib/cn';
import type { LeaseUrgency } from '../lease';
import { computeLeaseCountdown } from '../lease';

const URGENCY_STYLE: Record<LeaseUrgency, { color: string; bg: string }> = {
  ok: { color: 'var(--ink-2)', bg: 'var(--surface-muted)' },
  warning: { color: 'var(--warning)', bg: 'var(--warning-bg)' },
  critical: { color: 'var(--danger)', bg: 'var(--danger-bg)' },
  expired: { color: 'var(--danger)', bg: 'var(--danger-bg)' },
};

export interface QueueLeaseIndicatorProps {
  /** ISO devolvido pelo servidor (claim/heartbeat) — `null` quando não há reserva ativa. */
  claimExpiresAt: string | null;
  /** `true` quando o servidor já confirmou (409) que o lease foi perdido — sobrepõe o countdown local. */
  leaseLost: boolean;
  className?: string;
}

/** Tick de 1s local — só para o número descer; a verdade sobre "perdido" vem do servidor (heartbeat), nunca deste tick. */
function useNowTick(intervalMs: number): Date {
  const [now, setNow] = React.useState(() => new Date());
  React.useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

export function QueueLeaseIndicator({
  claimExpiresAt,
  leaseLost,
  className,
}: QueueLeaseIndicatorProps): React.JSX.Element | null {
  const now = useNowTick(1000);

  if (claimExpiresAt === null && !leaseLost) return null;

  const countdown = computeLeaseCountdown(claimExpiresAt, now);
  const urgency = leaseLost ? 'expired' : countdown.urgency;
  const label = leaseLost ? 'Reserva perdida — busque um novo cliente' : countdown.label;
  const style = URGENCY_STYLE[urgency];

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'inline-flex items-center gap-2 rounded-pill px-3 py-1.5 font-sans text-xs font-semibold',
        className,
      )}
      style={{ color: style.color, background: style.bg, boxShadow: 'var(--elev-1)' }}
    >
      <svg
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        className="w-3.5 h-3.5 shrink-0"
        aria-hidden="true"
      >
        <circle cx="8" cy="8" r="6.2" />
        <path d="M8 4.6V8l2.4 1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span>{label}</span>
    </div>
  );
}
